import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { RefObject } from 'react';
import {
  DateHeaderV10,
  ContentTimelineItemV10,
} from '@/components/dashboard-shared/ContentTimelinePanelV10';
import { DirectorShootingCard } from '@/components/staff/DirectorShootingCard';
import { CsmTaskCard } from '@/components/staff/CsmTaskCard';
import { StaffVideoCard } from '@/components/staff/StaffVideoCard';
import { ShootingEditorialCard } from './ShootingEditorialCard';
import type { CalendarEvent } from '@/services/business/calendar';
import type { Task } from '@/stores/calendarStore';
import type { StaffCalendarEvent, DirectorCalendarEvent } from '@/hooks/useStaffCalendarData';
import type { CsmCalendarEvent } from '@/hooks/useCsmCalendarData';
import {
  isStaffTaskEvent,
  isDirectorShootingEvent,
  isCsmTaskEvent,
} from './types';

interface CalendarDateGroupProps {
  dateKey: string;
  events: CalendarEvent[];
  isMobile: boolean;
  isFirstFutureDate: boolean;
  todayRef?: RefObject<HTMLDivElement>;
  showYear: boolean;
  eventTasks: Array<{ eventId: string; tasks: Task[] }>;
  staffRole: string | null;
  onEventClick: (event: CalendarEvent) => void;
  onOpenEditorialBooking: (event: CalendarEvent) => void;
  onShowTopicsModal: (data: {
    shootingId: string;
    shootingTitle?: string;
    shootingDate?: Date;
  }) => void;
}

export function CalendarDateGroup({
  dateKey,
  events,
  isMobile,
  isFirstFutureDate,
  todayRef,
  showYear,
  eventTasks,
  staffRole,
  onEventClick,
  onOpenEditorialBooking,
  onShowTopicsModal,
}: CalendarDateGroupProps) {
  const date = events[0].date;

  return (
    <motion.div
      key={dateKey}
      variants={{
        hidden: { opacity: 0, y: 16 },
        show: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.35, ease: [0, 0, 0.58, 1] },
        },
      }}
      className={cn('flex mb-8 relative scroll-mt-36', isMobile ? 'gap-3' : 'gap-6')}
      ref={isFirstFutureDate ? todayRef : undefined}
    >
      {/* Date column (left) */}
      <div
        className={cn(
          'flex flex-col shrink-0',
          isMobile ? 'w-[60px] items-end' : 'w-[90px] items-start',
        )}
      >
        <DateHeaderV10
          date={date}
          showYear={showYear}
          compact={isMobile}
          align={isMobile ? 'right' : 'left'}
        />
        <motion.div
          className={cn(
            'w-px flex-1 min-h-[20px] bg-[hsl(var(--v10-border))] mt-2',
            isMobile ? 'mr-[14px]' : 'ml-[22px]',
          )}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          style={{ transformOrigin: 'top' }}
        />
      </div>

      {/* Cards column (right) */}
      <div className="flex-1 min-w-0 flex flex-col gap-3 pt-1">
        {events.map((event) => {
          if (isDirectorShootingEvent(event as any)) {
            const directorEvent = event as unknown as DirectorCalendarEvent;
            return (
              <DirectorShootingCard
                key={event.id}
                shooting={directorEvent.shootingData}
              />
            );
          }

          if (isCsmTaskEvent(event as any)) {
            const csmEvent = event as unknown as CsmCalendarEvent;
            return <CsmTaskCard key={event.id} task={csmEvent.csmTaskData} />;
          }

          if (isStaffTaskEvent(event as any)) {
            const staffEvent = event as unknown as StaffCalendarEvent;
            return (
              <StaffVideoCard
                key={event.id}
                task={staffEvent.staffTaskData}
                staffRole={staffRole as 'video_editor' | 'monteur' | null}
              />
            );
          }

          if (event.type === 'content' || event.type === 'ab_testing') {
            return (
              <ContentTimelineItemV10
                key={event.calendarId ?? event.id}
                video={event}
                onClick={() => onEventClick(event)}
              />
            );
          }

          // SHOOTING / EDITORIAL
          const tasks = eventTasks.find((et) => et.eventId === event.id)?.tasks ?? [];
          return (
            <ShootingEditorialCard
              key={event.id}
              event={event}
              tasks={tasks}
              onClick={() => onEventClick(event)}
              onOpenEditorialBooking={onOpenEditorialBooking}
              onShowTopicsModal={onShowTopicsModal}
            />
          );
        })}
      </div>
    </motion.div>
  );
}
